import { Component, Input, Output, EventEmitter, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { CustomField, ProductV2Variation } from '../../../core/models/product-v2.model';
import { CurrencyBRLPipe } from '../../pipes/currency-brl.pipe';
import { ImageFallbackPipe } from '../../pipes/image-fallback.pipe';
import { I18nPipe } from '../../pipes/i18n.pipe';
import { InnerHtmlPipe } from '../../pipes/inner-html.pipe';
import { BuyNowRedirectService } from '../../../core/services/buy-now-redirect.service';
import { CartFacade } from '../../../core/store/cart/cart.facade';
import {
  isRegistryDomainProductUuid,
  isRegistryDomainVariationUuid,
} from '../../../core/utils/cart-domain-suggestion-rules';
import { filterTelemedicineVariationPlans } from '../../../core/utils/telemedicine-product-variations.util';
import {
  getMindPromoUiFromCustomFields,
  MindPromoUi,
} from '../../../core/utils/mind-price.utils';

@Component({
  selector: 'umkt-product-card',
  standalone: true,
  imports: [CommonModule, FormsModule, CurrencyBRLPipe, ImageFallbackPipe, I18nPipe, InnerHtmlPipe],
  templateUrl: './product-card.component.html',
  styleUrls: ['./product-card.component.scss'],
})
export class ProductCardComponent implements OnInit {
  @Input() imageUrl: string = '';
  @Input() title: string = '';
  @Input() description: string = '';
  @Input() plans: ProductV2Variation[] = [];
  @Input() price: number = 0;
  @Input() priceLabel: string = 'A partir de';
  @Input() planLabel: string = 'components.productCard.planLabelDefault';
  @Input() showAddToCart: boolean = false;
  @Input() showQuantitySelector: boolean = false;
  @Input() showLearnMore: boolean = true;
  @Input() showImage = true;
  @Input() buyNowLink: string = '';
  @Input() productUuid: string = '';
  @Input() mode: 'full' | 'reduced' = 'full';
  @Input() productType: string = '';
  @Input() productSku: string = '';
  @Input() customFields: CustomField[] | null = null;
  @Input() addToCartTextOverride: string = '';
  @Input() learnMoreTextOverride: string = '';
  @Input() badge = '';

  @Output() learnMore = new EventEmitter<string>();

  @Output() addToCart = new EventEmitter<{
    plan?: ProductV2Variation | null;
    quantity: number;
  }>();

  quantity: number = 1;
  selectedPlan: ProductV2Variation | null = null;
  displayPrice: number = 0;
  showPriceLabel: boolean = true;
  showSelectError: boolean = false;
  isSelectFocused: boolean = false;
  mindPromo: MindPromoUi | null = null;
  private cartFacade = inject(CartFacade);
  private buyNowRedirectService = inject(BuyNowRedirectService);
  mindCategoryLocked = false;
  private router = inject(Router);

  ngOnInit() {
    if (this.productType === 'MIND') {
      this.mindPromo = getMindPromoUiFromCustomFields(this.customFields);
    }

    if (this.title && ['starbem', 'telemedicina', 'telemedicine'].includes(this.title.trim().toLowerCase())) {
      this.plans = filterTelemedicineVariationPlans(this.plans || []);
    }

    if (this.plans.length > 0) {
      this.plans = this.plans.map(plan => {
        if (this.isRegistryDomainPlan(plan)) {
          return { ...plan };
        }

        let adjustedPrice = plan.price;
        if (plan.paymentRecurrence === 'annual') {
          adjustedPrice = plan.price / 12;
        }
        return {
          ...plan,
          price: adjustedPrice,
        };
      });

      const minPrice = Math.min(...this.plans.map(plan => plan.price));
      this.displayPrice = minPrice;
    } else {
      this.displayPrice = this.mindPromo?.displayPrice ?? this.price;
      this.showPriceLabel = false;
    }

    if (this.productType === 'MIND' && this.productSku) {
      this.cartFacade.getCartItems().subscribe(items => {
        this.mindCategoryLocked = items.some(
          i =>
            i.product?.productType === 'MIND' &&
            i.product?.sku === this.productSku
        );
      });
    }
  }

  get showMindPromotion(): boolean {
    return this.productType === 'MIND' && !!this.mindPromo?.hasPromotion;
  }

  get mindOriginalPriceFormatted(): string {
    return this.formatPrice(this.mindPromo?.originalPrice ?? 0);
  }

  get mindPromoDurationKey(): string {
    const months = this.mindPromo?.promotionMonths ?? 0;
    return months === 1
      ? 'components.productCard.promoFirstMonth'
      : 'components.productCard.promoFirstMonths';
  }

  get mindPromoDurationParams(): Record<string, string | number> {
    return { count: this.mindPromo?.promotionMonths ?? 0 };
  }

  get mindPromoAfterParams(): Record<string, string | number> {
    return { price: this.mindOriginalPriceFormatted };
  }

  updateDisplayPrice() {
    if (this.selectedPlan && this.plans.length > 0) {
      this.displayPrice = this.selectedPlan.price * this.quantity;
      this.showPriceLabel = false;
      this.showSelectError = false;
    } else {
      this.displayPrice = this.price * this.quantity;
      this.showPriceLabel = false;
      this.showSelectError = false;
    }
  }

  onPlanChange() {
    this.quantity = 1;
    this.showSelectError = false;
    this.showPriceLabel = false;
    this.updateDisplayPrice();
  }

  onSelectFocus() {
    this.isSelectFocused = true;
  }

  onSelectBlur() {
    this.isSelectFocused = false;
  }

  increment() {
    if (this.productType === 'MIND') return;
    if (!this.selectedPlan && this.plans.length > 0) {
      this.showSelectError = true;
      return;
    } else {
      this.quantity++;
      this.updateDisplayPrice();
    }
  }

  decrement() {
    if (!this.selectedPlan && this.plans.length > 0) {
      this.showSelectError = true;
      return;
    } else {
      if (this.quantity > 1) {
        this.quantity--;
        this.updateDisplayPrice();
      }
    }
  }

  onAddToCart() {
    if (this.buyNowLink) {
      this.buyNowRedirectService.open(this.buyNowLink);
      return;
    }

    if (!this.selectedPlan && this.plans.length > 0) {
      this.showSelectError = true;
      return;
    } else if (this.plans.length === 0) {
      this.addToCart.emit({
        plan: null,
        quantity: this.quantity,
      });
    } else {
      this.addToCart.emit({
        plan: this.selectedPlan,
        quantity: this.quantity,
      });
    }
  }

  onLearnMore() {
    this.learnMore.emit();
  }

  onLearnMoreClick(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.onLearnMore();
  }

  onImageError(event: Event) {
    const target = event.target as HTMLImageElement;
    if (target) {
      target.src = 'https://mkt-image-front.s3.us-east-1.amazonaws.com/not-found.png';
    }
  }

  formatPrice(price: number): string {
    return price.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  shouldShowPlanLabel(): boolean {
    if (!this.selectedPlan) {
      return false;
    }
    if (this.isRegistryDomainPlan(this.selectedPlan)) {
      return false;
    }
    return this.selectedPlan.paymentRecurrence === 'annual';
  }

  isRegistryDomainProduct(): boolean {
    if (isRegistryDomainProductUuid(this.productUuid)) {
      return true;
    }
    return (this.plans ?? []).some(plan => isRegistryDomainVariationUuid(plan.uuid));
  }

  isRegistryDomainPlan(plan: ProductV2Variation | null): boolean {
    if (plan && isRegistryDomainVariationUuid(plan.uuid)) {
      return true;
    }
    return isRegistryDomainProductUuid(this.productUuid);
  }

  getPriceLabelKey(): string {
    return this.isRegistryDomainProduct()
      ? 'components.productCard.annualPrice'
      : 'components.productCard.monthlyPrice';
  }
}
